// src/components/EnquiryRedeemBy.jsx
import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import "./EnquiryResults.css";
import useEscapeToGoBack from "../hooks/useEscapeToGoBack";

const API_LIST = "https://www.lianfoong.com/api/pawn-transactions/redeemed/by-redeem-by";

const fmt = (s) => {
  if (!s) return "-";
  const z = String(s).split("T")[0].split("-");
  return z.length === 3 ? `${z[2]}/${z[1]}/${z[0]}` : s;
};
const money = (n) => Number(n || 0).toFixed(2);

export default function EnquiryRedeemBy() {
  useEscapeToGoBack();

  const [nric, setNric] = useState("");
  const [rows, setRows] = useState(null);
  const [err, setErr] = useState("");
  const inputRef = useRef(null);

  useEffect(() => {
    inputRef.current?.focus();
    inputRef.current?.select?.();
  }, []);

  const onSearch = async () => {
    const v = (nric || "").trim().toUpperCase().replace(/\s+/g, "");
    if (!v) {
      setErr("Enter Redeemer NRIC, e.g. S1234567A");
      return;
    }
    try {
      setErr("");
      setRows(null);
      const r = await axios.get(API_LIST, { params: { nric: v } });
      setRows(r.data || []);
    } catch (e) {
      setErr(e?.response?.data || "Failed to load");
      setRows([]);
    }
  };

  const totals = useMemo(() => {
    if (!rows) return { count: 0, principal: 0, collected: 0, interest: 0 };
    const principal = rows.reduce((a, r) => a + Number(r.amount || 0), 0);
    const collected = rows.reduce((a, r) => a + Number(r.redeemAmount || 0), 0);
    const interest = rows.reduce((a, r) => a + Number(r.interest || 0), 0);
    return { count: rows.length, principal, collected, interest };
  }, [rows]);

  // ✅ NEW: Extract a single consistent Pawner header (only if unique)
  const pawnerHeader = useMemo(() => {
    if (!rows || rows.length === 0) return null;

    const uniq = (arr) => Array.from(new Set(arr.filter(Boolean)));

    const nrics = uniq(rows.map((r) => String(r.pawnerNirc || "").trim().toUpperCase()));
    const names = uniq(rows.map((r) => String(r.pawnerName || "").trim()));

    // Only show when it’s consistent
    if (nrics.length === 1 && names.length === 1) {
      return { pawnerNric: nrics[0], pawnerName: names[0] };
    }
    return null;
  }, [rows]);

  return (
    <div className="enquiryResults">
      <div className="row space-between">
        <h2>Redeemed — Search by Redeem By (NRIC)</h2>

        <div className="row" style={{ gap: 12 }}>
          <input
            ref={inputRef}
            className="input"
            placeholder="e.g. S1234567A"
            value={nric}
            onChange={(e) => setNric(e.target.value.toUpperCase())}
            onKeyDown={(e) => e.key === "Enter" && onSearch()}
            style={{ minWidth: 220 }}
          />
          <button className="btn primary" onClick={onSearch}>Search</button>
        </div>
      </div>

      {err && <div className="alert error">{String(err)}</div>}

      {!rows ? (
        <div style={{ marginTop: 12 }}>Enter redeemer NRIC and press Search…</div>
      ) : rows.length === 0 ? (
        <div className="empty">No redeemed tickets found for: {nric}</div>
      ) : (
        <>
          {/* ✅ NEW: show consistent Pawner header */}
          {pawnerHeader && (
            <div
              className="stats"
              style={{
                marginTop: 12,
                justifyContent: "flex-start",
                gap: 18,
              }}
            >
              <div className="stat">
                NRIC: <b>{pawnerHeader.pawnerNric}</b>
              </div>
              <div className="stat">
                Name: <b>{pawnerHeader.pawnerName}</b>
              </div>
            </div>
          )}

          <div className="stats" style={{ marginTop: 12 }}>
            <div className="stat">Count: <b>{totals.count}</b></div>
            <div className="stat">Principal: <b>${money(totals.principal)}</b></div>
            <div className="stat">Collected: <b>${money(totals.collected)}</b></div>
          </div>

          <div className="table-wrapper">
            <table className="table">
              <thead>
  <tr>
    <th>No</th>
    <th>Old Receipt</th>
    <th>Receipt</th>
    <th>Grant Date</th>
    <th>Redeem Date</th>
    <th className="xt-right">Amount</th>
    <th className="xt-right">Redeem</th>
    <th>Item</th>
  </tr>
</thead><tbody>
  {rows.map((r, i) => (
    <tr key={r.receiptNo ?? i}>
      <td>{i + 1}</td>
      <td>{(r.oldReceiptNo && String(r.oldReceiptNo).trim()) ? r.oldReceiptNo : "-"}</td>
      <td>{r.receiptNo}</td>
      <td>{fmt(r.grantDate)}</td>
      <td>{fmt(r.redeemDate)}</td>
      <td className="xt-right">{money(r.amount)}</td>
      <td className="xt-right">{money(r.redeemAmount)}</td>
      <td className="item-cell">{r.item}</td>
    </tr>
  ))}
</tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
